import { useMemo } from "react";
import { useRouter, useSearchParams } from "next/navigation";

import { Action, Category } from "@/constants/events";
import { useEventTracker } from "@/hooks/useEventTracker";

import DropdownV2 from "@/components/botanic/DropdownV2";

const ShopSort: React.FC<{ showEmptyState?: boolean; loading?: boolean }> = ({
  showEmptyState,
  loading,
}) => {
  const query = useSearchParams();
  const { push } = useRouter();
  const { publishEvent } = useEventTracker();

  const showSimilarStrains =
    query?.get("shopCategory") === "strain" &&
    ["on", "menu_items_only"].includes(query?.get("show_similar") || "");

  const sortOptions: Record<
    string,
    { label: string; params: Record<string, string> }
  > = {
    recommended: { label: "Recommended", params: {} },
    ...(!showSimilarStrains && {
      price_asc: {
        label: "Price Low-High",
        params: { sort: "price", sort_direction: "asc" },
      },
      price_desc: {
        label: "Price High-Low",
        params: { sort: "price", sort_direction: "desc" },
      },
      thc_content_asc: {
        label: "THC Low-High",
        params: { sort: "thc_content", sort_direction: "asc" },
      },
      thc_content_desc: {
        label: "THC High-Low",
        params: { sort: "thc_content", sort_direction: "desc" },
      },
    }),
    deal_active: {
      label: "Deals (% savings high to low)",
      params: { deal_active: "true" },
    },
  };

  const getSortSelection = useMemo(() => {
    if (query?.get("deal_active") === "true") {
      return "deal_active";
    }

    const sort = query?.get("sort");
    const sort_direction = query?.get("sort_direction");
    if (sort && sort_direction) {
      return `${sort}_${sort_direction}`;
    }

    return "recommended";
  }, [query]);

  const getSortText = (option: string) => sortOptions[option].label;

  const handleSelectSortOption = (option: string) => {
    const searchParams = new URLSearchParams(query?.toString());
    const params = sortOptions[option].params;

    // Clear out any existing sort params
    searchParams.delete("sort");
    searchParams.delete("sort_direction");
    searchParams.delete("deal_active");

    // Add the new sort params
    Object.entries(params).forEach(([key, value]) => {
      searchParams.set(key, value);
    });

    push(`/search/shop?${searchParams.toString()}`);

    publishEvent({
      action: Action.click,
      category: Category.resultsSorted,
      label: getSortText(option).toLowerCase(),
    });
  };

  return showEmptyState ? (
    <></>
  ) : (
    <div className="flex justify-end">
      <DropdownV2
        loading={loading}
        label="Sort by"
        variant="small"
        preselectedOption={getSortSelection}
        options={Object.keys(sortOptions)}
        onItemRender={(option) => ({
          children: getSortText(option),
          onClick: () => handleSelectSortOption(option),
        })}
      />
    </div>
  );
};

export default ShopSort;
